import { datadogRum } from "@datadog/browser-rum";
import { debug as debugFn } from "debug";

import { isBrowserExtensionError } from "@thalamos/frontend";
import type { ExtendedVisionUser } from "@thalamos/vision-common";

import { config } from "./config/index.js";

const debug = debugFn("vision-frontend:tracing");

export function initialiseTracing() {
  if (!config.datadog.disableTracing) {
    // The SDK's defaults are not stable across majors, so every option is set
    // explicitly rather than inherited. Some already match today's default and
    // are pinned anyway, so a future flip cannot change our behaviour silently.
    datadogRum.init({
      applicationId: config.datadog.applicationId,
      clientToken: config.datadog.clientToken,
      site: "datadoghq.eu",
      service: "vision",
      env: (config.environment as string | undefined) ?? "unknown",
      version: config.version,
      trackUserInteractions: false,
      allowedTracingUrls: [config.backendUrl],

      // Enabled, this adds a `baggage` request header that the servers' CORS
      // allowHeaders do not list, so the call fails preflight — costing the
      // request itself, not just its trace. The shipped typings document a
      // `false` default that the runtime does not honour.
      propagateTraceBaggage: false,

      trackResources: true,
      trackLongTasks: true,

      // Deliberately stricter than the SDK default, which masks only user input.
      defaultPrivacyLevel: "mask",

      // An error thrown entirely by a browser extension is not ours to fix.
      beforeSend: (event) => !isBrowserExtensionError(event),
    });
  }
}

export function setUser(user: ExtendedVisionUser | null) {
  if (!config.datadog.disableTracing) {
    if (user) {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        shoulderNumber: user.shoulderNumber,
      });
    } else {
      datadogRum.clearUser();
    }
  } else if (user) {
    debug("Datadog tracing is disabled but would set user to", {
      id: user.id,
      name: user.name,
      email: user.email,
      shoulderNumber: user.shoulderNumber,
    });
  } else {
    debug("Datadog tracing is disabled and user is null");
  }
}

export function logErrorToDatadog(error: Error) {
  debug(error);
  if (!config.datadog.disableTracing) {
    datadogRum.addError(error);
  }
}

export function logActionToDatadog(name: string, context?: object) {
  debug(name, context);
  if (!config.datadog.disableTracing) {
    datadogRum.addAction(name, context);
  }
}
